import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

export default function Home() {
  const [noirDrops, setNoirDrops] = useState([]);
  const [rose, setRose] = useState(null);
  const [quietAffection, setQuietAffection] = useState(null);
  const [leJardin, setLeJardin] = useState(null);
  const spotlightRef = useRef(null);
  const [spotlightInView, setSpotlightInView] = useState(false);

  useEffect(() => {
    if (!leJardin || !spotlightRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setSpotlightInView(true); obs.disconnect(); }
      },
      { threshold: 0.2 }
    );
    obs.observe(spotlightRef.current);
    return () => obs.disconnect();
  }, [leJardin]);

  const reveal = (delay = "") =>
    spotlightInView ? `animate-fade-in-up ${delay}` : "opacity-0";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allRes = await axios.get(`${API}/products`);
        setNoirDrops(allRes.data.filter((p) => p.drop_month));
        const roseProduct = allRes.data.find(p => p.name === "Rosé Classique");
        if (roseProduct) setRose(roseProduct);
        const qa = allRes.data.find(p => p.name === "Quiet Affection");
        if (qa) setQuietAffection(qa);
        const lji = allRes.data.find(p => p.name === "Le Jardin Impérial");
        if (lji) setLeJardin(lji);
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
  }, []);

  // Find "The Note" (signature size) to feature as hero
  const signatureDrop = noirDrops.find(d => d.name === "The Note") || noirDrops[0];

  return (
    <div className="page-transition" data-testid="home-page">
      <Seo
        title="Minneapolis Luxury Florist & Flower Delivery | Fleuris Noir"
        description="Shop luxury flower delivery in Minneapolis from Fleuris Noir. Discover premium Ecuadorian rose bouquets, 50 roses for $225, wedding florals, and corporate gifting."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://fleurisnoir.com/" },
          ],
        }}
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: signatureDrop 
              ? `url('${signatureDrop.image_url}')` 
              : `url('https://images.unsplash.com/photo-1647644520784-2a4bd03a4204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9vZHklMjBmbG9yYWwlMjBhcnJhbmdlbWVudHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="overline mb-6 animate-fade-in">Minneapolis &amp; the Twin Cities</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#f8f5f0] mb-5 animate-fade-in animate-delay-100">
            Minneapolis Luxury Florist<br />
            <span className="gold-gradient">&amp; Flower Delivery</span>
          </h1>
          <p className="font-heading text-lg md:text-xl italic text-[#b8966a]/90 mb-5 animate-fade-in animate-delay-200">
            Floral Compositions for the Quietly Opulent
          </p>
          <p className="font-body text-base md:text-lg text-[#a19d98] mb-10 max-w-2xl mx-auto animate-fade-in animate-delay-300">
            Fleuris Noir creates luxury rose bouquets and floral arrangements for delivery in
            Minneapolis and the Twin Cities. Discover premium Ecuadorian roses, thoughtful gifts,
            and bespoke florals for weddings, events, and corporate occasions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animate-delay-300">
            <Link to="/collection" className="btn-primary" data-testid="hero-shop-btn">
              Shop Bouquets
            </Link>
            {rose && (
              <Link to={`/collection/${rose.id}`} className="btn-secondary" data-testid="hero-50roses-btn">
                Shop 50 Roses — $225
              </Link>
            )}
            <Link to="/inquiries" className="btn-secondary" data-testid="hero-weddings-btn">
              Inquire: Weddings &amp; Events
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-[#b8966a]/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-[#b8966a] rounded-full" />
          </div>
        </div>
      </section>

      {/* Spotlight — Le Jardin Impérial (Statement Piece) */}
      {leJardin && (
        <section ref={spotlightRef} className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="home-le-jardin-spotlight">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${leJardin.image_url}')` }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0c0c0cf2 0%, #0c0c0cd9 40%, #0c0c0c55 100%)" }} />
          <div className="container-custom relative z-10 py-24">
            <div className="max-w-xl">
              <p className={`overline mb-5 ${reveal()}`}>The Statement · Le Jardin Collection</p>
              <h2 className={`font-display text-4xl sm:text-5xl lg:text-6xl text-[#f8f5f0] leading-[1.05] mb-6 ${reveal("animate-delay-100")}`}>
                Le Jardin <span className="gold-gradient">Impérial</span>
              </h2>
              <p className={`font-body text-lg text-[#d8d3cc] leading-relaxed mb-6 max-w-lg ${reveal("animate-delay-200")}`}>
                A grand expression of romance and abundance—50 premium roses, 15 lush peonies, and a breathtaking
                halo of hydrangeas, composed for the woman whose presence deserves nothing less than extraordinary.
              </p>
              <p className={`font-heading text-base italic text-[#b8966a]/90 mb-8 max-w-md ${reveal("animate-delay-300")}`}>
                "For the woman whose presence deserves a bouquet as magnificent as she is."
              </p>
              <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-5 ${reveal("animate-delay-400")}`}>
                <Link
                  to={`/collection/${leJardin.id}`}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="home-le-jardin-btn"
                >
                  Order Le Jardin Impérial
                  <ArrowRight size={14} />
                </Link>
                <span className="font-sans text-xl text-[#b8966a]">${leJardin.price?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Featured — The Thought of You Collection */}
      {quietAffection && (
        <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10" data-testid="home-thought-of-you">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div className="image-zoom aspect-[4/5] overflow-hidden order-2 md:order-1">
                <img src={quietAffection.image_url} alt={quietAffection.name} className="w-full h-full object-cover" />
              </div>
              <div className="order-1 md:order-2">
                <p className="overline mb-4">The Thought of You Collection</p>
                <h2 className="font-display text-3xl md:text-5xl text-[#f8f5f0] mb-5 leading-[1.1]">
                  {quietAffection.name}
                </h2>
                <p className="font-body text-[#a19d98] leading-relaxed mb-6">
                  Petite bouquets designed to celebrate meaningful moments, thoughtful gestures, and everyday
                  expressions of care—because sometimes the most meaningful gestures are the quietest ones.
                </p>
                <p className="font-sans text-2xl text-[#b8966a] mb-8">
                  ${quietAffection.price?.toFixed(2)}
                </p>
                <Link
                  to={`/collection/${quietAffection.id}`}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="home-quiet-affection-btn"
                >
                  Send a Thought
                  <ArrowRight size={14} />
                </Link>
                <Link
                  to="/collection?tag=Thought%20of%20You"
                  className="block mt-5 font-sans text-xs tracking-[0.2em] uppercase text-[#b8966a] hover:text-[#f8f5f0] transition-colors"
                  data-testid="home-thought-of-you-browse"
                >
                  Explore the Collection →
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* May Capsule Collection — Featured Signature Drop */}
      {signatureDrop && (
        <section className="bg-[#161616] border-b border-[#b8966a]/10" data-testid="home-noir-hero">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Image — The Note (Signature) */}
            <div className="relative group overflow-hidden">
              <img
                src={signatureDrop.image_url}
                alt={signatureDrop.name}
                className="w-full h-full object-cover min-h-[400px] lg:min-h-[600px] transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute top-6 left-6 bg-[#0c0c0c]/80 backdrop-blur-sm px-4 py-2 border border-[#b8966a]/30">
                <div className="flex items-center gap-2">
                  <Sparkles size={12} className="text-[#b8966a]" />
                  <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#b8966a]">
                    {signatureDrop.drop_month} Capsule Collection
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="bg-[#0c0c0c] p-10 md:p-16 flex flex-col justify-center">
              <p className="overline mb-3">The Signature Capsule</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#f8f5f0] italic mb-4">
                {signatureDrop.tagline || "A Love Written in Peonies..."}
              </h2>
              <p className="font-body text-[#a19d98] leading-relaxed mb-6">
                {signatureDrop.description}
              </p>
              <div className="flex items-end gap-4 mb-3">
                <p className="font-sans text-3xl text-[#b8966a]">
                  ${signatureDrop.price?.toFixed(2)}
                </p>
                <p className="font-body text-sm text-[#a19d98] pb-1">
                  {signatureDrop.name}
                </p>
              </div>
              <p className="font-body text-xs text-[#a19d98] mb-8">
                Available in 3 sizes starting at $85
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={`/collection/${signatureDrop.id}`}
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="home-noir-order-btn"
                >
                  Order Now
                  <ArrowRight size={14} />
                </Link>
                <Link
                  to="/collection"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="home-noir-explore-btn"
                >
                  View All Sizes
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All 3 Capsule Sizes */}
      {noirDrops.length > 1 && (
        <section className="section bg-[#0c0c0c]" data-testid="home-capsule-sizes">
          <div className="container-custom">
            <div className="text-center mb-12">
              <p className="overline mb-4">{noirDrops[0]?.drop_month} Collection</p>
              <h2 className="font-heading text-2xl md:text-3xl text-[#f8f5f0]">
                Choose Your Size
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {noirDrops.map((drop, index) => (
                <Link
                  key={drop.id}
                  to={`/collection/${drop.id}`}
                  className="group border border-[#b8966a]/10 hover:border-[#b8966a]/30 transition-all bg-[#161616]"
                  data-testid={`home-noir-product-${index}`}
                >
                  <div className="overflow-hidden">
                    <img
                      src={drop.image_url}
                      alt={drop.name}
                      className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="font-heading text-xl text-[#f8f5f0] mb-1 group-hover:text-[#b8966a] transition-colors">
                      {drop.name}
                    </h3>
                    <p className="font-body text-xs text-[#a19d98] mb-3">
                      {drop.description.split('—')[0]}
                    </p>
                    <p className="font-sans text-lg text-[#b8966a]">
                      ${drop.price?.toFixed(2)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Rosé — Signature Bouquet */}
      {rose && (
        <section className="section bg-[#161616] border-y border-[#b8966a]/10">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div className="image-zoom aspect-[3/4] overflow-hidden">
                <img src={rose.image_url} alt={rose.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="overline mb-4">Signature Bouquet</p>
                <h2 className="font-heading text-3xl md:text-4xl text-[#f8f5f0] mb-4">
                  {rose.name}
                </h2>
                <p className="font-body text-[#a19d98] leading-relaxed mb-6">
                  {rose.description}
                </p>
                <p className="font-sans text-2xl text-[#b8966a] mb-2">
                  From ${rose.price?.toFixed(2)}
                </p>
                <p className="font-body text-sm text-[#a19d98] mb-8">
                  Available in 25, 35, 50, 75, 100, or 150 roses
                </p>
                <Link to={`/collection/${rose.id}`} className="btn-primary inline-flex items-center gap-2" data-testid="home-rose-btn">
                  Order Rosé
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Memberships CTA */}
      <section className="relative overflow-hidden border-y border-[#b8966a]/10" style={{ background: "linear-gradient(135deg, #14100c 0%, #0c0c0c 60%, #1a1410 100%)" }} data-testid="home-memberships-cta">
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle, #b8966a 0%, transparent 70%)" }} />
        <div className="container-custom relative z-10 py-20 md:py-28 text-center max-w-3xl mx-auto">
          <p className="overline mb-4">Fleuris Noir Memberships</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#f8f5f0] mb-5 leading-[1.1]">
            Luxury Florals, <span className="gold-gradient">On Repeat</span>
          </h2>
          <p className="font-body text-base md:text-lg text-[#a19d98] mb-10 max-w-xl mx-auto">
            Recurring floral deliveries, white-glove concierge service, and corporate installations — tailored to your style and space.
          </p>
          <Link to="/memberships" className="btn-primary inline-flex items-center gap-2" data-testid="home-memberships-btn">
            Explore Memberships <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Bespoke */}
            <Link to="/bespoke" className="group relative h-[400px] overflow-hidden" data-testid="service-bespoke">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_dark-blooms/artifacts/eb4ti64h_8CD501EC-F66C-4659-8940-DBE486D67B38.png')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="overline mb-2">Personalized Design</p>
                <h3 className="font-heading text-2xl text-[#f8f5f0] mb-2 group-hover:text-[#b8966a] transition-colors">
                  Bespoke Arrangements
                </h3>
                <p className="font-body text-[#a19d98] text-sm">Custom creations tailored to your unique vision</p>
              </div>
            </Link>

            {/* Floral Bar */}
            <Link to="/floral-bar" className="group relative h-[400px] overflow-hidden" data-testid="service-floral-bar">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_dark-blooms/artifacts/iiw2ryzs_B99A06A8-E263-4967-9A76-6B59119D3EA1.jpeg')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="overline mb-2">Mobile Pop-Up Experience</p>
                <h3 className="font-heading text-2xl text-[#f8f5f0] mb-2 group-hover:text-[#b8966a] transition-colors">
                  The Floral Bar
                </h3>
                <p className="font-body text-[#a19d98] text-sm">We bring the beauty of floral design to your event</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote/Philosophy */}
      <section className="section bg-[#161616]">
        <div className="container-custom max-w-3xl text-center">
          <p className="font-heading text-2xl md:text-3xl text-[#f8f5f0] leading-relaxed italic">
            "We do not chase trends. We curate atmosphere. Each composition is designed as an editorial object — 
            not arranged, but authored."
          </p>
          <p className="overline mt-8">
            <Link to="/manifesto" className="hover:text-[#cfae82] transition-colors">
              Read Our Manifesto
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
